import { createBrowserRouter } from "react-router-dom";

/**
 * Public section of the app — routes that are reachable WITHOUT a
 * Firebase session or a selected tenant. Authorisation comes from a
 * signed link (HMAC `exp` + `sig` in the URL, verified by the backend),
 * the same idea as the file proxy.
 *
 * `main.tsx` picks this router instead of the authenticated
 * `DashboardShell` when the current path starts with one of
 * {@link PUBLIC_PATH_PREFIXES}. Keep this list short and explicit — every
 * prefix here bypasses the login screen.
 *
 * Routes:
 *   /av/erfassung/:forwardId?exp&sig — "Erfassung starten" confirmation
 *     page behind the button in the AV forward mail (Kundenwunsch
 *     M. Schenke 2026-09-03).
 */
export const PUBLIC_PATH_PREFIXES = ["/av/erfassung"] as const;

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

const lazyErrorBoundary = async () => {
  const { ErrorRenderer } = await import("shared-lib");
  return ErrorRenderer;
};

export const publicRouter = createBrowserRouter([
  {
    path: "/av/erfassung/:forwardId",
    lazy: {
      Component: async () =>
        (await import("../pages/public/av_erfassung/av_erfassung_page")).default,
      ErrorBoundary: lazyErrorBoundary,
    },
  },
  {
    // Anything else under a public prefix (e.g. a truncated link) shows
    // the same page in its "Link ungültig" state instead of the login.
    path: "/av/erfassung/*",
    lazy: {
      Component: async () =>
        (await import("../pages/public/av_erfassung/av_erfassung_page")).default,
      ErrorBoundary: lazyErrorBoundary,
    },
  },
]);
